import React, { useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { markSessionEnded } from './utils/sessionEnded';
import ErrorBoundary from './components/ErrorBoundary';
import DashboardLayout from './layouts/DashboardLayout';
import RequireAdmin from './components/RequireAdmin';
import LegalAccessGate from './components/LegalAccessGate';
import { homeForRole, isDistributorAdmin } from './utils/roles';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OrderManagement from './pages/OrderManagement';
import SalesRepresentatives from './pages/SalesRepresentatives';
import MyHub from './pages/MyHub';
import Doctors from './pages/Doctors';
import ShipmentTracking from './pages/ShipmentTracking';
import Reports from './pages/Reports';
import DistributorReports from './pages/DistributorReports';
import IVRDetails from './pages/IVRDetails';
import Products from './pages/Products';
import Settings from './pages/Settings';
import RegisterUsers from './pages/RegisterUsers';
import ContentManagement from './pages/ContentManagement';
import AuditLogs from './pages/AuditLogs';
import Invoices from './pages/Invoices';
import Statements from './pages/Statements';
import Clinics from './pages/Clinics';
import DeletionRequests from './pages/DeletionRequests';
import Integrations from './pages/Integrations';
import Users from './pages/Users';
import HelpCenter from './pages/HelpCenter';
import ResourceCenter from './pages/ResourceCenter';
import DistributorPortal from './pages/DistributorPortal';
import DistributorClinicShipments from './pages/DistributorClinicShipments';
import AdminDistributorBank from './pages/AdminDistributorBank';
import AdminDistributorPaymentTest from './pages/AdminDistributorPaymentTest';
import AdminDistributorSetup from './pages/AdminDistributorSetup';
import AdminDistributorActivity from './pages/AdminDistributorActivity';
import AdminDistributorOverview from './pages/AdminDistributorOverview';

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, logout, user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // HIPAA: Auto-logout after 30 minutes of inactivity.
  // No window.alert(): it is a modal browser dialog that blocks script execution, and it sat over
  // the login page looking like an error. The reason is written for Login to show as a banner,
  // BEFORE logout() so it is already there when the redirect renders the login page.
  const handleTimeout = useCallback(() => {
    markSessionEnded('inactivity');
    logout();
    if (isDistributorAdmin(user)) navigate('/distributor/login', { replace: true });
  }, [logout, navigate, user]);

  useInactivityTimeout(isAuthenticated ? handleTimeout : null);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={
          pathname === '/distributor' || pathname.startsWith('/distributor/')
            ? '/distributor/login'
            : '/login'
        }
        replace
      />
    );
  }

  return <LegalAccessGate>{children}</LegalAccessGate>;
}

/**
 * Protected routes fail closed.
 *
 * Every route is wrapped in the role guard. New pages remain admin-only unless listed in the
 * explicit rep or distributor allowlist; a distributor never inherits a rep route.
 */
export const PROTECTED_ROUTES = [
  { path: '/', element: <RoleHome /> },
  { path: '/distributor', element: <DistributorPortal /> },
  { path: '/distributor/customers', element: <DistributorPortal /> },
  { path: '/distributor/orders', element: <DistributorPortal /> },
  { path: '/distributor/ivrs', element: <DistributorPortal /> },
  { path: '/distributor/shipments', element: <DistributorPortal /> },
  { path: '/distributor/bank', element: <DistributorPortal /> },
  { path: '/distributor/resources', element: <ResourceCenter /> },
  { path: '/distributor/help', element: <HelpCenter /> },
  { path: '/distributor/reports', element: <DistributorReports /> },
  { path: '/distributor/settings', element: <Settings /> },
  { path: '/distributor-clinic/shipments', element: <DistributorClinicShipments /> },
  { path: '/admin/distributor-bank', element: <AdminDistributorBank /> },
  { path: '/admin/distributor-payment-test', element: <AdminDistributorPaymentTest /> },
  { path: '/admin/distributors', element: <AdminDistributorSetup /> },
  { path: '/admin/distributor-overview', element: <AdminDistributorOverview /> },
  { path: '/admin/distributor-customers', element: <AdminDistributorActivity kind="customers" /> },
  { path: '/admin/distributor-resources', element: <AdminDistributorActivity kind="resources" /> },
  { path: '/admin/distributor-reports', element: <AdminDistributorActivity kind="reports" /> },
  { path: '/admin/distributor-orders', element: <AdminDistributorActivity kind="orders" /> },
  { path: '/admin/distributor-ivrs', element: <AdminDistributorActivity kind="ivrs" /> },
  { path: '/admin/distributor-shipments', element: <AdminDistributorActivity kind="shipments" /> },
  { path: '/orders', element: <OrderManagement /> },
  { path: '/sales-reps', element: <SalesRepresentatives /> },
  { path: '/my-hub', element: <MyHub /> },
  // My Team became the Team tab of My Hub (9/4/26). Its old path remains rep-visitable.
  { path: '/my-team', element: <Navigate to="/my-hub" replace /> },
  { path: '/shipments', element: <ShipmentTracking /> },
  { path: '/registrations', element: <RegisterUsers /> },
  { path: '/registrations/:id', element: <RegisterUsers /> },
  { path: '/reports', element: <Reports /> },
  { path: '/ivr-details', element: <IVRDetails /> },
  { path: '/invoices', element: <Invoices /> },
  // Statements is absent from both non-admin allowlists.
  { path: '/statements', element: <Statements /> },
  { path: '/clinics', element: <Clinics /> },
  { path: '/products', element: <Products /> },
  { path: '/doctors', element: <Doctors /> },
  { path: '/content', element: <ContentManagement /> },
  { path: '/audit-logs', element: <AuditLogs /> },
  { path: '/deletion-requests', element: <DeletionRequests /> },
  { path: '/users', element: <Users /> },
  { path: '/integrations', element: <Integrations /> },
  { path: '/settings', element: <Settings /> },
  { path: '/help-center', element: <HelpCenter /> },
  { path: '/resources', element: <ResourceCenter /> },
];

function RoleHome() {
  const { user } = useAuth();
  return user?.role === 'admin' && !isDistributorAdmin(user) ? (
    <Dashboard />
  ) : (
    <Navigate to={homeForRole(user)} replace />
  );
}

/** Every protected page checks the current role against its own route. */
export function guardedElement(path, element) {
  const base = path.replace(/\/:.*$/, '');
  return <RequireAdmin path={base}>{element}</RequireAdmin>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/distributor/login" element={<Login />} />
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {PROTECTED_ROUTES.map(({ path, element }) => (
            <Route key={path} path={path} element={guardedElement(path, element)} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
